/**
 * ACCOUNT-level Plant register DTOs for the mobile app (MP-16, #2281).
 *
 * Mobile mirror of the web account surface (`GET/POST /api/plant-assets`,
 * `GET/PATCH /api/plant-assets/[assetId]`, `POST /api/plant-assets/upload`).
 * The PROJECT-scoped mobile contracts (MobileProjectPlant*) stay in
 * `plant.ts` untouched — different surface, different role gate.
 *
 * Envelope roots AND the shared leaf schemas reused from `plant.ts` are
 * deliberately NOT `.strict()` (issue #2411): an installed build parses a
 * response with the schema it was COMPILED against, so a server-added field
 * must be ignored, not fail the parse. The server-boundary `*Strict`
 * variants beside each base in `plant.ts` restore the over-serialisation
 * guard wherever a route re-parses what it serialises.
 *
 * Writes reuse the SAME request schemas as the web routes
 * (`PlantAssetCreateRequest` / `PlantAssetUpdateRequest` in `plant.ts`), so
 * "what a valid asset write is" cannot drift between the two surfaces.
 */
import { z } from "zod";
import {
  PlantAssetEventSchema,
  PlantAssetListItemSchema,
  PlantAssetListItemStrict,
  PlantAssetRequirementSchema,
  PlantAssetRequirementStrict,
  PlantAssetSchema,
  PlantAssetStrict,
  PlantComplianceResultSchema,
  PlantComplianceResultStrict,
  PlantEvidenceSchema,
  PlantEvidenceStrict,
  PlantAssetEventStrict,
  PlantOutOfServiceRequest,
  PlantProjectStatusSchema,
  PlantRegisterSummaryStrict,
  PlantQuarantineRequest,
  PlantRegisterSummarySchema,
  PlantReturnToServiceRequest,
} from "./plant";

/** The asset's live placement (ON_SITE / QUARANTINED), if any. */
export const MobilePlantCurrentPlacementSchema = z.object({
  assignmentId: z.string(),
  projectId: z.string(),
  projectTitle: z.string(),
  status: PlantProjectStatusSchema,
  siteLocation: z.string().nullable(),
});
export type MobilePlantCurrentPlacement = z.infer<
  typeof MobilePlantCurrentPlacementSchema
>;
export const MobilePlantCurrentPlacementStrict =
  MobilePlantCurrentPlacementSchema.strict();

/**
 * One filtered page of the account register plus the register-wide summary.
 * Same shape as the web `PlantAssetListResponse`: `summary` reflects the
 * WHOLE active register (fleet totals) while `assets` carries the filtered
 * page.
 */
export const MobilePlantRegisterListResponse = z.object({
  assets: z.array(PlantAssetListItemSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
  summary: PlantRegisterSummarySchema,
});
export type MobilePlantRegisterListResponse = z.infer<
  typeof MobilePlantRegisterListResponse
>;
export const MobilePlantRegisterListResponseStrict =
  MobilePlantRegisterListResponse.extend({
    assets: z.array(PlantAssetListItemStrict),
    summary: PlantRegisterSummaryStrict,
  }).strict();

/**
 * The register detail a phone needs for the overview + edit screen:
 * identity/photo, derived compliance, the live placement, the requirement +
 * evidence summaries, and recent history events. Requirement/evidence WRITE
 * flows (MP-17) and transitions (MP-18) are separate later surfaces.
 */
export const MobilePlantAssetDetailResponse = z.object({
  asset: PlantAssetSchema,
  compliance: PlantComplianceResultSchema.nullable(),
  currentAssignment: MobilePlantCurrentPlacementSchema.nullable(),
  requirements: z.array(PlantAssetRequirementSchema),
  evidence: z.array(PlantEvidenceSchema),
  events: z.array(PlantAssetEventSchema),
});
export type MobilePlantAssetDetailResponse = z.infer<
  typeof MobilePlantAssetDetailResponse
>;
export const MobilePlantAssetDetailResponseStrict =
  MobilePlantAssetDetailResponse.extend({
    asset: PlantAssetStrict,
    compliance: PlantComplianceResultStrict.nullable(),
    currentAssignment: MobilePlantCurrentPlacementStrict.nullable(),
    requirements: z.array(PlantAssetRequirementStrict),
    evidence: z.array(PlantEvidenceStrict),
    events: z.array(PlantAssetEventStrict),
  }).strict();

/** Create + core-field edit responses: the persisted asset, photo presigned. */
export const MobilePlantAssetMutationResponse = z.object({
  asset: PlantAssetSchema,
});
export type MobilePlantAssetMutationResponse = z.infer<
  typeof MobilePlantAssetMutationResponse
>;
export const MobilePlantAssetMutationResponseStrict = z
  .object({ asset: PlantAssetStrict })
  .strict();
/** Replay may return the deliberately minimal id stub when the asset was deleted. */
export const MobilePlantAssetMutationReplayResponseStrict = z.union([
  MobilePlantAssetMutationResponseStrict,
  z.strictObject({
    asset: z.strictObject({ id: z.string(), photoUrl: z.null() }),
  }),
]);

/**
 * Direct multipart photo upload result — the mobile mirror of the web
 * `/api/plant-assets/upload` response. BOTH fields are needed by the create:
 * the key is what the read path presigns, a private URL alone renders 403.
 */
export const MobilePlantAssetPhotoUploadResponse = z.object({
  url: z.string(),
  key: z.string(),
  fileName: z.string(),
  contentType: z.string(),
});
export type MobilePlantAssetPhotoUploadResponse = z.infer<
  typeof MobilePlantAssetPhotoUploadResponse
>;

/* ------------------------------------------------------------------ *
 * Availability transitions (MP-18, #2283)
 * ------------------------------------------------------------------ */

/**
 * The account-register availability transitions, as ONE mobile route with an
 * action union instead of the web's five sibling routes — the mobile surface
 * prefers a single endpoint, but each action keeps 1:1 semantics with its web
 * route by REUSING that route's request schema verbatim below, so the reason
 * / notes / checked-by requirements cannot drift between the two surfaces.
 *
 * The actions and their guards are exactly the web set:
 * `archive` / `retire` (terminal, confirm-only), `quarantine` (reason
 * required), `out-of-service` (reason optional), `return-to-service`
 * (competent-person notes + name required).
 */
export const MobilePlantTransitionRequest = z.discriminatedUnion("action", [
  z.strictObject({ action: z.literal("archive") }),
  z.strictObject({ action: z.literal("retire") }),
  PlantQuarantineRequest.extend({ action: z.literal("quarantine") }),
  PlantOutOfServiceRequest.extend({
    action: z.literal("out-of-service"),
  }),
  PlantReturnToServiceRequest.extend({
    action: z.literal("return-to-service"),
  }),
]);
export type MobilePlantTransitionRequest = z.infer<
  typeof MobilePlantTransitionRequest
>;

/* ------------------------------------------------------------------ *
 * Plant reminder settings (MP-18, #2283)
 * ------------------------------------------------------------------ */

/** The account's Plant notification preferences (spec §22.4). */
export const MobilePlantNotificationSettings = z.object({
  digestEnabled: z.boolean(),
  includeAdmins: z.boolean(),
  includeSupervisors: z.boolean(),
  criticalEmailEnabled: z.boolean(),
  expiryWarningDays: z.number().int(),
});
export type MobilePlantNotificationSettings = z.infer<
  typeof MobilePlantNotificationSettings
>;

export const MobilePlantNotificationSettingsResponse = z.object({
  settings: MobilePlantNotificationSettings,
});
export type MobilePlantNotificationSettingsResponse = z.infer<
  typeof MobilePlantNotificationSettingsResponse
>;

/**
 * Requirements + evidence workflow (MP-17, #2282): the mobile mirror of
 * `GET/POST /api/plant-assets/[assetId]/requirements`,
 * `DELETE …/requirements/[requirementId]` and the evidence review lifecycle
 * (`GET/POST …/evidence`, `PATCH/approve/reject …/evidence/[evidenceId]`).
 * Writes reuse the SAME request schemas as the web routes
 * (`PlantRequirementCreateRequest`, `PlantEvidenceCreateRequest`,
 * `PlantEvidenceUpdateRequest`, `PlantEvidenceRejectRequest` in `plant.ts`);
 * evidence rows carry NO file URLs — `hasFile` + the presign-on-read
 * `…/evidence/[evidenceId]/file` endpoint, exactly as on the web.
 */
export const MobilePlantRequirementListResponse = z.object({
  requirements: z.array(PlantAssetRequirementSchema),
});
export type MobilePlantRequirementListResponse = z.infer<
  typeof MobilePlantRequirementListResponse
>;

export const MobilePlantRequirementMutationResponse = z.object({
  requirement: PlantAssetRequirementSchema,
});
export type MobilePlantRequirementMutationResponse = z.infer<
  typeof MobilePlantRequirementMutationResponse
>;

export const MobilePlantEvidenceListResponse = z.object({
  evidence: z.array(PlantEvidenceSchema),
});
export type MobilePlantEvidenceListResponse = z.infer<
  typeof MobilePlantEvidenceListResponse
>;

export const MobilePlantEvidenceMutationResponse = z.object({
  evidence: PlantEvidenceSchema,
});
export type MobilePlantEvidenceMutationResponse = z.infer<
  typeof MobilePlantEvidenceMutationResponse
>;

// Full-depth server-boundary variants. The client-facing bases above remain
// permissive so installed app versions ignore additive server fields.
export const MobilePlantAssetPhotoUploadResponseStrict =
  MobilePlantAssetPhotoUploadResponse.strict();
export const MobilePlantNotificationSettingsResponseStrict =
  MobilePlantNotificationSettingsResponse.extend({
    settings: MobilePlantNotificationSettings.strict(),
  }).strict();
export const MobilePlantRequirementListResponseStrict = z
  .object({ requirements: z.array(PlantAssetRequirementStrict) })
  .strict();
export const MobilePlantRequirementMutationResponseStrict = z
  .object({ requirement: PlantAssetRequirementStrict })
  .strict();
export const MobilePlantRequirementReplayResponseStrict = z.union([
  MobilePlantRequirementMutationResponseStrict,
  z.strictObject({ requirementId: z.string() }),
]);
export const MobilePlantEvidenceListResponseStrict = z
  .object({ evidence: z.array(PlantEvidenceStrict) })
  .strict();
export const MobilePlantEvidenceMutationResponseStrict = z
  .object({ evidence: PlantEvidenceStrict })
  .strict();
export const MobilePlantEvidenceReplayResponseStrict = z.union([
  MobilePlantEvidenceMutationResponseStrict,
  z.strictObject({ evidenceId: z.string() }),
]);
